/**
 * A bug, a wrong assumption in the engine, a camera we got wrong — sent
 * straight to an inbox. See lib/net/feedback.ts for why this is the one place
 * in the app that talks to a network.
 */

import { useRef, useState } from "react";

import { FEEDBACK_MESSAGE_MAX, sendFeedback } from "../lib/net/feedback";
import { Turnstile } from "./Turnstile";

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
}

type Status = "idle" | "busy" | "sent" | "error";

export function FeedbackDialog({ open, onClose }: FeedbackDialogProps) {
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  // Deliberately NOT React state: a bot that fills every input it finds in
  // the rendered DOM catches this one. A bot driving this component through
  // React (calling setHoneypot itself) is not what this defends against —
  // that would require scripting the app's own code, not just its markup.
  const honeypotRef = useRef<HTMLInputElement>(null);

  const close = () => {
    onClose();
    // Reset once the close animation/transition would be done, not before —
    // an instant clear would flash an empty form during the close.
    setTimeout(() => {
      setMessage("");
      setEmail("");
      setStatus("idle");
      setError(null);
      setTurnstileToken(null);
    }, 200);
  };

  const handleOpen = (el: HTMLDialogElement | null) => {
    dialogRef.current = el;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  };

  async function submit() {
    if (!message.trim()) return;
    setStatus("busy");
    setError(null);
    const result = await sendFeedback(
      message,
      email,
      honeypotRef.current?.checked ?? false,
      turnstileToken,
    );
    if (result.ok) {
      setStatus("sent");
    } else {
      setStatus("error");
      setError(result.message);
    }
  }

  return (
    <dialog
      ref={handleOpen}
      onClose={onClose}
      className="m-auto w-[min(94vw,480px)] rounded-[3px] border-2 border-graphite bg-paper p-0 text-graphite backdrop:bg-graphite/50"
    >
      <div className="border-b border-slate-line px-4 py-3">
        <h2 className="text-[22px] font-bold uppercase leading-none">Send feedback</h2>
        <p className="mt-1 text-[13px] text-graphite-2">
          A bug, a camera we got wrong, a rule that does not match your water. Goes straight to an
          inbox, no account needed.
        </p>
      </div>

      {status === "sent" ? (
        <div className="px-4 py-6 text-center">
          <p className="m-0 text-[15px] font-semibold">Sent — thanks.</p>
          <p className="m-0 mt-1 text-[13px] text-graphite-2">
            If you left an email, expect a reply there.
          </p>
          <button
            type="button"
            onClick={close}
            className="mt-4 cursor-pointer rounded-[2px] border-[1.5px] border-graphite bg-transparent px-3.5 py-[7px] font-display text-[15px] font-semibold uppercase tracking-[0.06em] text-graphite"
          >
            Close
          </button>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="grid gap-3.5 px-4 py-3.5">
            <label className="grid gap-1">
              <span className="font-display text-[13px] font-semibold uppercase tracking-[0.16em] text-graphite-2">
                Message
              </span>
              <textarea
                required
                autoFocus
                rows={5}
                value={message}
                maxLength={FEEDBACK_MESSAGE_MAX}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What happened, or what should this do instead?"
                className="resize-y border border-slate-line bg-field px-2 py-1.5 font-body text-sm"
              />
            </label>

            <label className="grid gap-1">
              <span className="font-display text-[13px] font-semibold uppercase tracking-[0.16em] text-graphite-2">
                Email <span className="normal-case tracking-normal text-graphite-2">(optional, for a reply)</span>
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="border border-slate-line bg-field px-2 py-1.5 font-body text-sm"
              />
            </label>

            {/* Honeypot. Clipped to a single pixel rather than display:none —
                some bots specifically skip fields a stylesheet has hidden but
                still find ones merely sized to nothing. `<dialog>` does not
                clip overflow on its own, so this must not rely on
                off-screen positioning either, or it can open a stray
                horizontal scrollbar. Never focusable, never announced. */}
            <input
              ref={honeypotRef}
              type="checkbox"
              name="botcheck"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="absolute h-px w-px overflow-hidden whitespace-nowrap p-0 opacity-0 [clip:rect(0,0,0,0)]"
            />

            <Turnstile onToken={setTurnstileToken} />

            {error && (
              <p className="m-0 border-l-[3px] border-flag bg-flag/10 px-2.5 py-1.5 text-[12.5px]">
                {error}
              </p>
            )}
          </div>

          <div className="flex flex-wrap justify-end gap-[7px] border-t border-slate-line px-4 py-3">
            <button
              type="button"
              onClick={close}
              className="cursor-pointer rounded-[2px] border-[1.5px] border-graphite bg-transparent px-3.5 py-[7px] font-display text-[15px] font-semibold uppercase tracking-[0.06em] text-graphite"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={status === "busy" || !message.trim() || !turnstileToken}
              className="cursor-pointer rounded-[2px] border-none bg-deep px-3.5 py-[7px] font-display text-[15px] font-semibold uppercase tracking-[0.06em] text-paper disabled:cursor-not-allowed disabled:opacity-40"
            >
              {status === "busy" ? "Sending…" : "Send"}
            </button>
          </div>
        </form>
      )}
    </dialog>
  );
}
